import * as Highcharts from 'highcharts';
import more from 'highcharts/highcharts-more';
import {Component, Inject, Input, OnInit} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';

import {CockpitCard} from 'shared/dtos/cockpit-card';
import {ChartTemplateService} from '../../../cockpit-shared/charts-templates/chart-template.service';
import {CockpitChartComponent} from '../../../cockpit-shared/cockpit-content-component';
import {
  KoefUndIntervalle,
  Series
} from 'shared/dtos/cockpit/charts/geschaetzteKoeffKonfidenzintervalle';

more(Highcharts);

@Component({
  selector: 'app-geschaetzte-koeffizienten-konfidenzintervalle',
  templateUrl:
    './chart-geschaetzte-koeffizienten-konfidenzintervalle.component.html',
  styleUrl: '../../../cockpit-shared/card/card.component.scss',
  providers: [ChartTemplateService],
  standalone: false
})
export class GeschaetzteKoeffizientenKonfidenzintervalleComponent
  extends CockpitChartComponent
  implements OnInit
{
  card: CockpitCard;
  koefUndIntervalle: KoefUndIntervalle;

  constructor(
    @Inject(ChartTemplateService)
    private readonly chartTemplateService: ChartTemplateService,
    private readonly translateService: TranslateService
  ) {
    super();
  }

  @Input() set geschaetzteKoeffKonfidenzintervalle(series: KoefUndIntervalle) {
    if (series) {
      this.koefUndIntervalle = series;
      this.initialize(this.koefUndIntervalle?.series);
    }
  }

  ngOnInit() {
    this.translateService.onLangChange.subscribe(() => {
      const svgContainer = document.getElementById(
        'svgChartKoeffKonfidenzintervalle'
      );
      if (svgContainer) {
        this.initialize(this.koefUndIntervalle.series);
      }
    });
  }

  exportChart() {
    this.chart.exportChart(
      {
        type: 'application/pdf',
        filename: 'my-pdf'
      },
      {}
    );
  }

  initialize(data: Series) {
    this.chartTemplateService.chartname = 'geschaetzteKoeffKonfidenzintervalle';
    this.chartTemplateService.json = this.getHighchartsOptions(data);
    this.chartTemplateService.initializeDefaultValues();

    // Define custom series type for displaying low/med/high values using boxplot as a base
    this.highChartsInitializer();

    // Create chart
    Highcharts.chart(
      'svgChartKoeffKonfidenzintervalle',
      this.chartTemplateService.json,
      thechart => {
        this.callBackFuncToNormalizeBarChart(thechart);

        // Observer pour détecter les changements de taille du conteneur
        const resizeObserver = new ResizeObserver(() => {
          if (this.chart) {
            this.chart.reflow();
          }
        });
        resizeObserver.observe(
          document.getElementById('svgChartKoeffKonfidenzintervalle')
        );
      }
    );

    // Remove click events on container to avoid having "clickable" announced by AT
    // These events are needed for custom click events, drag to zoom, and navigator
    // support.
    this.chart.container.onmousedown = null;
    this.chart.container.onclick = null;
  }

  private highChartsInitializer() {
    this.chartTemplateService.chartname = 'geschaetzteKoeffKonfidenzintervalle';
    const tooltipUntergrenze = this.translateService.instant(
      'm1.sprint10.cockpit.card.chart.kennzahlenZurMethodik.koefUndIntervalle.tooltip.untergrenze'
    );
    const tooltipObergrenze = this.translateService.instant(
      'm1.sprint10.cockpit.card.chart.kennzahlenZurMethodik.koefUndIntervalle.tooltip.obergrenze'
    );
    const tooltipSchaetzung = this.translateService.instant(
      'm1.sprint10.cockpit.card.chart.kennzahlenZurMethodik.koefUndIntervalle.tooltip.schaetzung'
    );

    // Define custom series type for displaying low/med/high values using boxplot as a base
    Highcharts.seriesType(
      'lowmedhigh',
      'boxplot',
      {
        keys: ['low', 'median', 'high'],
        tooltip: {
          pointFormat: `${tooltipUntergrenze} <b>{point.low}</b> ; ${tooltipSchaetzung} <b>{point.median}</b> ; ${tooltipObergrenze} <b>{point.high}</b><br/>`
        }
      },
      {
        // Change point shape to a line with three crossing lines for low/median/high
        // Stroke width is hardcoded to 1 for simplicity
        drawPoints() {
          // eslint-disable-next-line @typescript-eslint/no-this-alias, consistent-this
          const theSeries = this;
          this.points.forEach(point => {
            // eslint-disable-next-line no-lone-blocks
            {
              let {graphic} = point;
              const verb = graphic ? 'animate' : 'attr';
              const {shapeArgs} = point;
              const {width} = shapeArgs;
              const left = Math.floor(shapeArgs.x) + 0.5;
              // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
              const right = left + width;
              const crispX = left + Math.round(width / 2) + 0.5;
              const highPlot = Math.floor(point.highPlot) + 0.5;
              const medianPlot = Math.floor(point.medianPlot) + 0.5;
              const lowPlot =
                Math.floor(point.lowPlot) + 0.5 - (point.low === 0 ? 1 : 0); // Sneakily draw low marker even if 0

              if (point.isNull) {
                return;
              }

              if (!graphic) {
                // eslint-disable-next-line no-multi-assign
                point.graphic = graphic = theSeries.chart.renderer
                  .path('point')
                  .add(theSeries.group);
              }

              graphic.attr({
                stroke: point.color || theSeries.color,
                'stroke-width': 1
              });

              graphic[verb]({
                d: [
                  'M',
                  left,
                  highPlot,
                  'H',
                  right,
                  'M',
                  left,
                  medianPlot,
                  'H',
                  right,
                  'M',
                  left,
                  lowPlot,
                  'H',
                  right,
                  'M',
                  crispX,
                  highPlot,
                  'V',
                  lowPlot
                ]
              });
            }
          });
        }
      }
    );
  }

  private callBackFuncToNormalizeBarChart(thechart: Highcharts.Chart) {
    this.chart = thechart;
    thechart.reflow();
  }

  private getHighchartsOptions(serie: Series) {
    return {
      chart: {
        type: 'lowmedhigh',
        inverted: true
      },
      legend: {
        enabled: false
      },

      title: {
        text: this.translateService.instant(
          'm1.sprint10.cockpit.card.chart.kennzahlenZurMethodik.koefUndIntervalle.title'
        )
      },
      subtitle: {
        text: this.translateService.instant(
          'm1.sprint11.cockpit.card.chart.kennzahlenZurMethodik.koefUndIntervalle.hinweis'
        ),
        verticalAlign: 'bottom',
        align: 'left'
      },
      accessibility: {
        point: {
          descriptionFormatter(point: any) {
            // Use default formatter for null points
            if (point.isNull) {
              return false;
            }

            return `${point.category}, low ${point.low}, median ${point.median}, high ${point.high}`;
          }
        },

        series: {
          descriptionFormatter(series) {
            return `${series.name}, series ${series.index + 1} of ${
              series.chart.series.length
            } with ${series.points.length} data points.`;
          }
        }
      },

      xAxis: {
        categories: [
          'Gender',
          'Train',
          'Exp',
          'Exp2',
          'Tnr',
          'Skl1',
          'Skl2',
          'Skl3',
          'Skl4',
          'Skl5',
          'Skl6',
          'Skl7',
          'Skl8',
          'Pos1',
          'Pos2',
          'Pos3',
          'Pos4',
          'Pos5'
        ]
      },

      yAxis: {
        title: {
          text: this.translateService.instant(
            'm1.sprint10.cockpit.card.chart.kennzahlenZurMethodik.koefUndIntervalle.x-Achse'
          )
        },
        plotLines: [
          {
            dashStyle: 'Dash',
            value: 0.0,
            width: 1.5,
            color: '#323232',
            zIndex: -1000
          }
        ]
      },
      tooltip: {
        shared: true
      },

      plotOptions: {
        series: {
          stickyTracking: true,
          whiskerWidth: 5
        }
      },

      series: [
        {
          name: serie.name,
          data: this.parseSeriesData(serie.data)
        }
      ] as Highcharts.SeriesOptionsType[]
    } as Highcharts.Options;
  }

  private parseSeriesData(data: string): Array<[number, number, number]> {
    try {
      const parsed: unknown = JSON.parse(data);
      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed.filter(
        (point): point is [number, number, number] =>
          Array.isArray(point) &&
          point.length === 3 &&
          point.every(value => typeof value === 'number')
      );
    } catch {
      return [];
    }
  }
}
